import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, Zap } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { PRICING_PLANS } from '../data/constants';
import { startCheckout, LOOKUP_KEYS } from '../lib/payments';
import { useToast } from '../hooks/use-toast';
import { PlanCard } from '../components/pricing/PlanCard';

const Pricing = () => {
  const { t } = useApp();
  const { user } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [loadingId, setLoadingId] = useState(null);

  const benefits = [
    { id: 'b1', icon: Shield, title: t('pricing.b1.t'), desc: t('pricing.b1.d') },
    { id: 'b2', icon: TrendingUp, title: t('pricing.b2.t'), desc: t('pricing.b2.d') },
    { id: 'b3', icon: Zap, title: t('pricing.b3.t'), desc: t('pricing.b3.d') },
  ];

  const onSelect = async (planId) => {
    if (planId === 'free') { nav(user ? '/post' : '/signin?mode=signup'); return; }
    if (!user) { nav(`/signin?next=${encodeURIComponent('/pricing')}`); return; }
    setLoadingId(planId);
    try {
      await startCheckout(LOOKUP_KEYS[planId], { userId: user.id });
    } catch (e) {
      console.error('Checkout failed', e);
      toast({ title: t('pricing.payError'), description: e?.response?.data?.detail || e.message });
      setLoadingId(null);
    }
  };

  return (
    <div data-testid="pricing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F8B5C]/10 text-[#0A6E47] text-xs font-semibold uppercase tracking-wider mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#0F8B5C]" /> {t('pricing.eyebrow')}</span>
        <h1 className="text-4xl md:text-6xl font-black tracking-tight text-[#1A1A1A]">{t('pricing.title')}</h1>
        <p className="text-lg text-[#7A7468] mt-4 max-w-2xl mx-auto">{t('pricing.subtitle')}</p>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {PRICING_PLANS.map((plan) => <PlanCard key={plan.id} plan={plan} loading={loadingId === plan.id} onSelect={onSelect} />)}
        </div>
        <div className="mt-16 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {benefits.map((b) => (
            <div key={b.id} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0F8B5C]/10 flex items-center justify-center shrink-0"><b.icon className="w-5 h-5 text-[#0F8B5C]" /></div>
              <div><div className="font-bold text-[#1A1A1A]">{b.title}</div><div className="text-sm text-[#7A7468] mt-0.5">{b.desc}</div></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
